"use client";

import * as React from "react";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

const tooltipVariants = {
  default: {
    content: "bg-background custom-shadow border-neutral-200 text-neutral-800 rounded-xl p-4",
    arrow: "bg-background fill-background",
  },
  rounded: {
    content: "bg-neutral-800 text-white border-none text-xs rounded-full px-3.5 py-1",
    arrow: "bg-neutral-800 fill-neutral-800",
  },
};

const arrowAlignStyles = {
  left: "left-4",
  center: "left-1/2 -translate-x-1/2",
  right: "right-4",
};

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  variant = "default",
  arrowAlign = "center",
  align,
  side = "top",
  fixed = false,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: keyof typeof tooltipVariants;
  arrowAlign?: keyof typeof arrowAlignStyles;
  fixed?: boolean;
}) {
  const variantStyles = tooltipVariants[variant];
  const isFixed = fixed || variant === "rounded";

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        align={align}
        side={side}
        avoidCollisions={!isFixed}
        className={cn(
          "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 w-fit max-w-96 origin-(--radix-tooltip-content-transform-origin) text-sm wrap-break-word break-keep",
          variantStyles.content,
          className
        )}
        {...props}
      >
        {children}
        {isFixed ? (
          <span
            className={cn(
              "absolute bottom-[1px] z-50 size-2.5 translate-y-1/2 rotate-45 rounded-[2px]",
              variantStyles.arrow,
              arrowAlignStyles[arrowAlign]
            )}
          />
        ) : (
          <TooltipPrimitive.Arrow
            className={cn(
              "z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]",
              variantStyles.arrow
            )}
          />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
